import { CloudOff, FileQuestion, Inbox, Lock, RefreshCw, ServerCrash } from 'lucide-react'
import type { ReactNode } from 'react'
import type { ApiError } from '@/api/errors'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from './Button'

type Kind = 'empty' | 'forbidden' | 'not-found' | 'error' | 'unavailable' | 'offline'

const icons = { empty: Inbox, forbidden: Lock, 'not-found': FileQuestion, error: ServerCrash, unavailable: CloudOff, offline: CloudOff }

type StatePanelProps = {
  kind: Kind
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
  compact?: boolean
}

export function StatePanel({ kind, title, description, action, className, compact }: StatePanelProps) {
  const Icon = icons[kind]
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'px-6 py-10' : 'px-6 py-20', className)}>
      <span className="mb-4 flex size-11 items-center justify-center rounded-[calc(14px*var(--radius-scale))] bg-hover text-muted ring-1 ring-line">
        <Icon className="size-5" strokeWidth={1.75} aria-hidden />
      </span>
      <h3 className="text-[length:calc(15px*var(--text-scale))] font-semibold text-fg">{title}</h3>
      {description && <p className="mt-1 max-w-[46ch] text-[length:calc(13.5px*var(--text-scale))] leading-relaxed text-muted">{description}</p>}
      {action && <div className="mt-5 flex gap-2">{action}</div>}
    </div>
  )
}

/** Maps an ApiError onto the right recovery surface. Filters stay intact on retry. */
export function ErrorState({ error, onRetry, compact }: { error: ApiError; onRetry?: () => void; compact?: boolean }) {
  const t = useT()
  const retry = onRetry && (
    <Button onClick={onRetry} icon={<RefreshCw className="size-3.5" />}>
      {t('common.actions.tryAgain')}
    </Button>
  )
  if (error.isForbidden) {
    return (
      <StatePanel
        compact={compact}
        kind="forbidden"
        title={t('errors.states.noAccessTitle')}
        description={t('errors.states.noAccessText')}
      />
    )
  }
  if (error.isNotFound) {
    return <StatePanel compact={compact} kind="not-found" title={t('errors.states.notFoundTitle')} description={error.message} />
  }
  if (error.code === 'NETWORK' || error.code === 'TIMEOUT') {
    return <StatePanel compact={compact} kind="offline" title={t('errors.states.connectionTitle')} description={error.message} action={retry} />
  }
  if (error.status === 503) {
    return <StatePanel compact={compact} kind="unavailable" title={t('errors.states.unavailableTitle')} description={error.message} action={retry} />
  }
  return (
    <StatePanel
      compact={compact}
      kind="error"
      title={t('errors.states.loadFailedTitle')}
      description={
        <>
          {error.message}
          {error.requestId && <span className="num mt-1 block text-[length:calc(12px*var(--text-scale))] text-subtle">{t('errors.states.requestId', { id: error.requestId })}</span>}
        </>
      }
      action={retry}
    />
  )
}
